import { Box, Paper, Typography, Grid, Skeleton, Chip, Button } from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  ArrowForward,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsApi } from '../../services/analyticsApi';

/**
 * Format currency from cents
 */
const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

interface MetricCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  trend?: number; // Percentage change
}

function MetricCard({ label, value, subtitle, trend }: MetricCardProps) {
  const getTrendIcon = () => {
    if (trend === undefined) return null;
    if (trend > 0) return <TrendingUp fontSize="small" color="success" />;
    if (trend < 0) return <TrendingDown fontSize="small" color="error" />;
    return <TrendingFlat fontSize="small" color="disabled" />;
  };

  const getTrendColor = () => {
    if (trend === undefined) return 'default';
    if (trend > 0) return 'success';
    if (trend < 0) return 'error';
    return 'default';
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" component="span" fontWeight="medium">
          {value}
        </Typography>
        {trend !== undefined && (
          <Chip
            icon={getTrendIcon() || undefined}
            label={`${trend > 0 ? '+' : ''}${trend}%`}
            size="small"
            color={getTrendColor() as 'default' | 'success' | 'error'}
            variant="outlined"
            sx={{ height: 24 }}
          />
        )}
      </Box>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function WidgetSkeleton() {
  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Skeleton width={120} height={28} />
        <Skeleton width={80} height={24} />
      </Box>
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Skeleton width={80} height={20} sx={{ mb: 1 }} />
            <Skeleton width={100} height={32} />
            <Skeleton width={60} height={16} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

export default function AnalyticsDashboardWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics', 'dashboard-summary'],
    queryFn: () => analyticsApi.getDashboardSummary(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return <WidgetSkeleton />;
  }

  if (error || !data) {
    return null; // Silently hide widget on error
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Analytics Overview</Typography>
        <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/analytics')}>
          View Details
        </Button>
      </Box>

      {/* Period indicator */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {data.period}
      </Typography>

      {/* Metrics Grid */}
      <Grid container spacing={3}>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Revenue"
            value={formatCurrency(data.revenue.total)}
            trend={data.revenue.changePercent}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Clients"
            value={data.clients.total}
            subtitle={`+${data.clients.newThisPeriod} new`}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Bookings"
            value={data.bookings.total}
            subtitle={`${data.bookings.completionRate}% completed`}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label="Hours Tracked"
            value={data.time.totalHours}
            subtitle={`${data.time.billablePercent}% billable`}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
